import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { debounce } from "lodash";
import {
	Button,
	Col,
	ConfigProvider,
	Form,
	Input,
	PageHeader,
	Row,
	Select,
	Table,
	Tooltip,
} from "antd";
import moment from "moment-jalaali";
import en_US from "antd/lib/locale-provider/en_US";

import { GetUrlParams, ObjectToURL } from "scripts/helpers";
import { setRoute } from "scripts/reducer";
import API from "scripts/api";
import { Modals } from "scripts/settings";

import InfoModal from "modals/TrackerInfoModal";

const Component = ({ onSetRoute, parentName, routeName }) => {
	const initialState = {
		attributes: [],
		data: [],
		pagination: {},
		tableColumns: [],
		modalData: {},
		users: [],
	};

	const [state, setState] = useState(initialState);
	const [filteredColumns, setFilteredColumns] = useState([]);
	const {
		attributes,
		data,
		loading,
		modalData,
		pagination,
		tableColumns,
		users,
	} = state;
	const { pathname, search } = useLocation();
	const [form] = Form.useForm();
	const navigate = useNavigate();

	const fetchData = async () => {
		const params = GetUrlParams(search);

		form.resetFields();
		form.setFieldsValue(params);

		setState((prevState) => ({ ...prevState, loading: true }));

		await Promise.all([
			API.user.list({ pageSize: 50 }),
			API.tracker.list({
				...params,
				pageSize: process.env.REACT_APP_PAGE_SIZE,
			}),
		])
			.then(
				([
					{ data: _users },
					{
						attributes,
						data,
						meta: { currentPage, perPage, total },
					},
				]) => {
					setState((prevState) => ({
						...prevState,
						users: _users.map(({ id, fullName }) => ({
							value: id,
							label: fullName,
						})),
						attributes: attributes,
						data,
						tableColumns: [
							{
								title: "row",
								dataIndex: "index",
								className: "center-cell",
								width: "60px",
								render: (value, record, index) => index + 1,
							},
							{
								title: "User",
								width: "120px",
								dataIndex: "user",
								className: "center-cell",
								render: (value) => typeof value === "object" && value?.fullName,
							},
							{
								title: "Ip Adress",
								dataIndex: "ipAddress",
								className: "center-cell ",
							},
							{
								title: "Title",
								dataIndex: "title",
								className: "center-cell",
							},
							{
								title: "Description",
								dataIndex: "description",
								className: "center-cell",
								render: (value) => {
									return (
										typeof value === "string" && (
											<Tooltip title={"More Info"}>
												<Button
													type="link"
													icon={<i className="fa fa-sticky-note" />}
													onClick={() =>
														handleModalData({ description: value })
													}
												/>
											</Tooltip>
										)
									);
								},
							},
							{
								title: "Date",
								dataIndex: "date",
								className: "center-cell desc-cell",
								render: (value) => {
									const [date, time] = moment(value)
										.format(process.env.REACT_APP_DATE_TIME_FORMAT_JALALI)
										.split(" ");

									return (
										<>
											<span className="name">{date}</span>
											<small className="desc">{time}</small>
										</>
									);
								},
							},
							{
								title: "Action",
								dataIndex: "action",
								className: "center-cell",
							},
							{
								title: "Permission",
								dataIndex: "permission",
								className: "center-cell",
								render: (value) => value?.name,
							},
							{
								title: "Target",
								dataIndex: "target",
								className: "center-cell",
								render: (value) => value?.name,
							},
							{
								title: "Request data",
								dataIndex: "requestData",
								className: "center-cell",
								render: (value) => {
									return (
										<Tooltip title={"More Info"}>
											<Button
												type="link"
												icon={<i className="fa fa-sticky-note" />}
												onClick={() => handleModalData(value)}
											/>
										</Tooltip>
									);
								},
							},
							{
								title: "Request url",
								dataIndex: "requestUrl",
								className: "center-cell",
							},
							{
								title: "Target data",
								dataIndex: "targetData",
								className: "center-cell",
								render: (value) => {
									return (
										<Tooltip title={"More Info"}>
											<Button
												type="link"
												icon={<i className="fa fa-sticky-note" />}
												onClick={() => handleModalData(value)}
											/>
										</Tooltip>
									);
								},
							},
						],

						pagination: {
							current: currentPage,
							pageSize: perPage,
							total,
							showSizeChanger: false,
						},
						loading: false,
					}));
				}
			)
			.catch(() => {
				setState((prevState) => ({ ...prevState, loading: false }));
			})
			.finally(() => {
				setState((prevState) => ({ ...prevState, loading: false }));
			});
	};

	const handleModalData = (value) => {
		navigate(`${pathname}#${Modals.INFO}`);

		setState((prevState) => ({ ...prevState, modalData: value }));
	};

	const handleSearch = debounce((search) => {
		setState((prevState) => ({ ...prevState, usersLoading: true }));
		if (search?.length > 1) {
			API.user
				.list({ paginate: false, search })
				.then(({ data }) => {
					setState((prevState) => ({
						...prevState,
						users: data.map((o) => ({
							value: o.id,
							label: `${o.firstName} ${o.lastName}`,
						})),
						usersLoading: false,
					}));
				})
				.catch(() => {});
		}
	}, 1000);

	const handleTable = ({ current }) => {
		const params = GetUrlParams(search);
		const query = ObjectToURL({ ...params, page: current });

		navigate(`${pathname}?${query}`);
	};

	const handleFilter = debounce(() => {
		form.validateFields().then((values) => {
			const search = ObjectToURL({ page: 1, ...values });

			navigate(`${pathname}?${search}`);
		});
	}, 1000);

	const componentDidUpdate = () => {
		fetchData();
	};

	const componentDidMount = () => {
		onSetRoute({ openKey: parentName, selectedKey: routeName });
		fetchData();
	};

	useEffect(componentDidUpdate, [search]);
	useEffect(componentDidMount, []);
	useEffect(() => {
		setFilteredColumns(
			tableColumns.filter((column) => attributes.includes(column.dataIndex))
		);
	}, [tableColumns]);

	return (
		<>
			<ConfigProvider direction="ltr" locale={en_US}>
				<PageHeader
					title="security reports"
					style={{ justifyContent: "flex-start" }}
				/>
				<Form layout="vertical" form={form} dir="ltr">
					<Row gutter={24}>
						<Col xs={24} sm={12} md={8} lg={6} xxl={4}>
							<Form.Item name="search">
								<Input
									suffix={<i className="fa fa-search" />}
									onChange={handleFilter}
									placeholder="search"
									allowClear
									autoFocus
								/>
							</Form.Item>
						</Col>
						<Col xs={24} sm={12} md={8} lg={6} xxl={4}>
							<Form.Item name="user">
								<Select
									placeholder="user"
									options={users}
									showSearch
									optionFilterProp="label"
									onSearch={handleSearch}
									onChange={handleFilter}
									allowClear
								/>
							</Form.Item>
						</Col>
					</Row>
				</Form>
				<Table
					columns={filteredColumns}
					style={{ direction: "ltr" }}
					dataSource={data}
					loading={loading}
					onChange={handleTable}
					pagination={pagination}
					scroll={{ x: 930 }}
					rowKey="id"
				/>
				<InfoModal data={modalData} />
			</ConfigProvider>
		</>
	);
};

export default connect(
	(state) => {
		return {};
	},
	(dispatch) => {
		return {
			onSetRoute: (data) => dispatch(setRoute(data)),
		};
	}
)(Component);
